/* eslint-disable */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import '../../styles/components.css';
import { useAuth } from '../../contexts/AuthContext';

const AcceptInvite: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const type = searchParams.get('type');
  const cname = searchParams.get('cname');

  const [loading, setLoading] = useState(true); // Start with loading true
  const [error, setError] = useState('');
  const [invitation, setInvitation] = useState<any>(null);
  const [user, setUser] = useState<any>(null);
  const [companyName, setCompanyName] = useState<string>('');

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    let mounted = true;

    // Check if user is authenticated (from Supabase invitation email)
    const loadData = async () => {
      try {
        // First, check if user is authenticated (Supabase invitation link auto-authenticates)
        const { data: { session } } = await supabase.auth.getSession();

        if (session?.user && mounted) {
          setUser(session.user);

          // CHECK PROFILE ROLE FIRST - if Company User, redirect to Company First Run
          const { data: profile } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', session.user.id)
            .maybeSingle();

          if (profile && (profile.role === 'Company User' || profile.role?.toLowerCase().includes('company'))) {
            console.log('User is a Company User, redirecting away from NGO signup...');
            navigate('/company/accept-invite', { replace: true });
            return;
          }

          // Use the secure RPC to fetch invitation details
          // This bypasses RLS issues that might block direct table reads
          const { data: rpcData, error: rpcError } = await supabase.rpc('get_my_invitation');

          if (rpcData?.success && mounted) {
            const inviteData = rpcData.invitation;
            setInvitation(inviteData);
            setCompanyName(inviteData.company_name);
            const { data: ngoUser } = await supabase
              .from('ngo_users')
              .select('ngo_id')
              .eq('user_id', session.user.id)
              .maybeSingle();
            
            if (ngoUser?.ngo_id) {
              console.log('User already linked, redirecting to dashboard');
              navigate(`/ngo/ngo/dashboard`, { replace: true });
              return;
            }

            // Update invitation with supabase_user_id if not already set
            if (!inviteData.supabase_user_id) {
              await supabase
                .from('ngo_invitations')
                .update({ supabase_user_id: session.user.id })
                .eq('id', inviteData.id);
            }
          } else if (mounted) {
            // Fallback to email search if RPC fails or is not found
            console.warn('RPC get_my_invitation failed, falling back to direct query');
            const { data: inviteByEmail } = await supabase
              .from('ngo_invitations')
              .select('*')
              .eq('ngo_email', session.user.email)
              .neq('status', 'cancelled')
              .order('invited_at', { ascending: false })
              .limit(1)
              .maybeSingle();

            if (inviteByEmail && mounted) {
              setInvitation(inviteByEmail);
              
              // Fetch company name separately to avoid complex join errors
              if (inviteByEmail.company_id) {
                if (cname && mounted) {
                  setCompanyName(cname);
                } else {
                  const { data: companyData } = await supabase
                    .from('companies')
                    .select('name')
                    .eq('id', inviteByEmail.company_id)
                    .maybeSingle();
                  if (companyData && mounted) {
                    setCompanyName(companyData.name);
                  }
                }
              }

              // Check linkage
              const { data: ngoUser } = await supabase.from('ngo_users').select('ngo_id').eq('user_id', session.user.id).maybeSingle();
              if (ngoUser?.ngo_id) {
                navigate(`/ngo/ngo/dashboard`, { replace: true });
                return;
              }
            } else if (mounted) {
              setError('No invitation found for your email. Please contact the company that invited you.');
            }
          }
        } else if (token && mounted) {
          // Fallback: try to load invitation by token (old method)
          const { data: inviteData } = await supabase
            .from('ngo_invitations')
            .select(`
              *,
              companies:company_id (
                name
              )
            `)
            .eq('invitation_token', token)
            .eq('status', 'pending')
            .maybeSingle();

          if (inviteData && mounted) {
            setInvitation(inviteData);
            // Extract company name
            if (inviteData.companies && Array.isArray(inviteData.companies) && inviteData.companies.length > 0) {
              setCompanyName(inviteData.companies[0].name);
            } else if (inviteData.companies && typeof inviteData.companies === 'object' && 'name' in inviteData.companies) {
              setCompanyName(inviteData.companies.name);
            } else if (inviteData.company_id) {
              if (cname && mounted) {
                setCompanyName(cname);
              } else {
                // Fallback: fetch company name separately
                const { data: companyData } = await supabase
                  .from('companies')
                  .select('name')
                  .eq('id', inviteData.company_id)
                  .maybeSingle();
                if (companyData && mounted) {
                  setCompanyName(companyData.name);
                }
              }
            }
            setError('Please click the link in your invitation email to continue. The link will automatically log you in.');
          } else if (mounted) {
            setError('Invalid or expired invitation. Please check your email for the latest invitation link.');
          }
        } else if (mounted) {
          setError('No invitation found. Please check your email for the invitation link from Supabase.');
        }
      } catch (err: any) {
        if (mounted) {
          console.error('Error checking auth:', err);
          setError('An error occurred. Please try again.');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    // Listen for auth state changes (when user clicks email link)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event: string, session: any) => {
      if (event === 'SIGNED_IN' && session?.user && mounted) {
        setUser(session.user);
        setLoading(true);
        // Reload invitation with the authenticated user
        await loadData();
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [token]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      setLoading(false);
      return;
    }

    try {
      // Check if user is authenticated
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();

      if (userError || !currentUser) {
        throw new Error('You must be logged in. Please click the link in your invitation email.');
      }

      if (!invitation) {
        throw new Error('Invalid invitation. Please contact the company that invited you.');
      }

      // Update password for the invited user
      const { error: updateError } = await supabase.auth.updateUser({
        password: formData.password
      });

      if (updateError) {
        throw updateError;
      }

      const user = currentUser;

      // ATOMIC SETUP: Use the backend RPC to handle profiles, NGOs, and linkage
      // This is much more reliable than manual client-side updates.
      // The invitation token is usually the Supabase user ID for invited users.
      const rpcToken = token || invitation?.invitation_token || user.id;

      const { data: setupData, error: setupError } = await supabase.rpc('setup_ngo_account', {
        user_id_param: user.id,
        invitation_token_param: rpcToken,
        email_param: user.email
      });

      if (setupError || !setupData?.success) {
        console.error('RPC Setup Error:', setupError || setupData?.error);
        throw new Error(setupData?.error || setupError?.message || 'Failed to link account');
      }

      const ngoId = setupData.ngo_id;

      // Force full page reload to dashboard or first-run
      // This ensures App.tsx re-hydrates with the new ngo_id linkage
      window.location.href = `/ngo/ngo/first-run`;
    } catch (err: any) {
      setError(err.message || 'Failed to set up account');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p style={{ marginTop: '1rem', color: 'var(--muted)' }}>Loading invitation...</p>
      </div>
    );
  }

  return (
    <div className="ngo-accept-page">
      <div className="form-container">
        <div className="form-section">
          <h1>Complete Your NGO Account Setup</h1>
          <p style={{ color: 'var(--muted)', marginBottom: '2rem' }}>
            Set your password to complete your account setup
          </p>

          {invitation && (
            <div style={{
              background: 'var(--secondary)',
              padding: '1.25rem',
              borderRadius: 'var(--radius)',
              marginBottom: '2rem',
              border: '1px solid var(--primary-light)'
            }}>
              {companyName ? (
                <p style={{ margin: 0, fontWeight: 600, color: 'var(--primary-dark)' }}>
                  Invited by: {companyName}
                </p>
              ) : (
                <p style={{ margin: 0, fontWeight: 600, color: 'var(--primary-dark)' }}>
                  You have been invited to join the platform
                </p>
              )}
              <p style={{ margin: '0.5rem 0 0 0', color: 'var(--muted)', fontSize: '0.9rem' }}>
                Email: {invitation.ngo_email}
              </p>
            </div>
          )}

          {user && (
            <div style={{
              background: '#d1fae5',
              padding: '1rem',
              borderRadius: 'var(--radius)',
              marginBottom: '2rem',
              border: '1px solid #10b981'
            }}>
              <p style={{ margin: 0, color: '#065f46', fontSize: '0.9rem' }}>
                ✓ You're logged in as {user.email}. Please set your password below.
              </p>
            </div>
          )}

          {error && (
            <div className="toast toast-error" style={{ position: 'relative', marginBottom: '1rem' }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Set Password *</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                placeholder="Minimum 8 characters"
                minLength={8}
              />
              <div className="help-text">
                Password must be at least 8 characters long
              </div>
            </div>

            <div className="form-group">
              <label>Confirm Password *</label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
                placeholder="Re-enter password"
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={loading || !invitation || !user}>
                {loading ? 'Setting Up...' : 'Set Password & Continue'}
              </button>
            </div>

            {!user && (
              <div style={{
                marginTop: '1rem',
                padding: '1rem',
                background: '#fef3c7',
                borderRadius: 'var(--radius)',
                border: '1px solid #f59e0b'
              }}>
                <p style={{ margin: 0, color: '#92400e', fontSize: '0.9rem' }}>
                  ⚠️ You need to click the link in your invitation email first. The email from Supabase will automatically log you in, then you can set your password here.
                </p>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite;


